import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import Page from '../../../components/Page';
import PageLayout from '../../../components/PageLayout/PageLayout';
import PageTitle from '../../../components/PageLayout/PageTitle';
import * as paths from '../../../routes/paths';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectIsEnterpriseUser,
  selectUserProfile,
} from '../../../selectors/user';
import {
  useLibrary,
  useOnboardingStatus,
  useRenameContent,
} from '../../../hooks';
import Spinner from '@raydiant/raydial/components/Spinner';
import LibraryPageActionButton from './components/LibraryPageActionButton';
import { useToastState } from '@raydiant/raydial/components/Toast';
import useCreateFolder from '../../../hooks/useCreateFolder';
import { Content, CreateFolder } from '@raydiant/api-client-js';
import {
  getQueryString,
  getSortedLibraryData,
  addQueryParamsToUrl,
  isSafeBackToPath,
  LibraryQueryParams,
} from '../utils';
import DomainProfiles from './DomainProfiles';
import { createNewId } from '../../../utilities/identifiers';
import {
  selectIsManageMode,
  selectLayoutOptions,
  selectMainSortOptions,
  selectSelectedNodeIds,
} from '../selectors';
import { selectLocalUploads } from '../../../selectors/fileUploads';
import Card from '@raydiant/raydial/components/Card';
import * as presentationPageActions from '../../PresentationPage/actions';
import { capitalize } from '../../../utilities/stringUtils';
import DeleteContentDialog from './components/DeleteContentDialog';
import CopyUnownedContentDialog from './components/CopyUnownedContentDialog';
import CreateFolderDialog from './components/CreateFolderDialog';
import LibraryOnboardingDialog from './components/LibraryOnboardingDialog';
import LibraryPageActionBar from './components/LibraryPageActionBar';
import EmptyLibrary from './components/EmptyLibrary';
import ListViewLayout from './components/ListViewLayout';
import GridViewLayout from './components/GridViewLayout';
import RenameContentDialog from './components/RenameContentDialog';
import ShareDialog from './components/ShareDialog';
import { BACK_BUTTON_ICON } from '../../LocationsPage/DeviceShare/DeviceShare';
import _, { xor } from 'lodash';
import useMoveAllItemsToFolder from '../../../hooks/useMoveAllItemsToFolder';
import {
  canDeleteContent,
  canEditContent,
  getResourceIdsFromNodeIds,
} from '../../../utilities';
import Link from '@raydiant/raydial/components/Link';
import * as actions from '../actions';
import * as addContentPageActions from '../../AddContentPage/actions';
import * as libraryTreeActions from '../../../components/LibraryTree/actions';
import PaperModalV2 from 'raydiant-elements/core/PaperModalV2';
import cn from 'classnames';
import MoveContent from './components/MoveContent';
import Error from '../../../components/Error/Error';
import useDeleteContent from '../../../hooks/useDeleteContent';
import useGetDomain from '../../../hooks/useGetDomain';
import miraClient from '../../../clients/raydiant';
import LibraryDragLayerV2 from '../../../components/LibraryDragLayer/LibraryDragLayerV2';
import useCopyContent from '../../../hooks/useCopyContent';
import { createDuplicateContentName } from '../../../utilities/getDuplicateResourceName';
import config from '../../../config';
import * as P from '../../../clients/mira/types/Presentation';
import * as presActions from '../../../actions/presentations';
import * as applicationsPageActions from '../../ApplicationsPage/actions';
import { DEFAULT_QUERY } from '../../AddContentPage/AddContentPageV2';
import { selectApplications } from '../../../selectors/applications';
import { selectThemesById } from '../../../selectors/v2/themes';
import {
  createPresentationFromAppVersion,
  getDefaultThemeForUser,
} from '../../../utilities';
import analytics from '../../../clients/analyticsClient';

export type LibraryPageModalType =
  | 'folder'
  | 'share'
  | 'rename'
  | 'delete'
  | 'move'
  | 'duplicate_and_move';

// Only support pictures app for file upload in LibraryPage
const picturesAppId = config.picturesAppId;

// Appended to a copy's success toast when the backend reports it created a
// "Supporting content" folder to hold copies of content the user didn't own.
const SUPPORTING_CONTENT_MESSAGE =
  ' We added a “Supporting content” folder with the extra copies it needs.';

const LibraryPageV2 = () => {
  const state = useToastState();
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  // File upload related refs and states
  const fileInput = useRef<HTMLInputElement>(null);

  // Router & URL
  const searchMatch = useRouteMatch({ path: paths.librarySearch.pattern });
  const sharedMatch = useRouteMatch({
    path: paths.librarySharedWithMe.pattern,
  });
  const sharedLibrariesMatch = useRouteMatch({
    path: paths.librarySharedLibraries.pattern,
  });
  const profilesMatch = useRouteMatch({
    path: paths.libraryUsersLibraries.pattern,
  });

  const queryParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      // Map current route to the API scope value used by the library query
      scope: sharedMatch
        ? 'shared'
        : sharedLibrariesMatch
        ? 'shared_library'
        : 'owned',
      profile_id: searchParams.get('profile_id') ?? '',
      search: searchParams.get('search') ?? '',
      folder_id: searchParams.get('folder_id') ?? '',
      sort_by: searchParams.get('sort_by') ?? '',
      sort_direction: searchParams.get('sort_direction') ?? '',
      // Sanitized at the parse boundary so every consumer (back control, and
      // any future back_to use) only ever sees an in-app path; an unsafe value
      // clears it, which also disables the back-to-playlist boundary.
      back_to: isSafeBackToPath(searchParams.get('back_to') ?? '')
        ? searchParams.get('back_to')!
        : '',
      back_to_folder: searchParams.get('back_to_folder') ?? '',
    };
  }, [location.search, sharedMatch, sharedLibrariesMatch]);

  const baseUrl = useMemo(() => {
    if (sharedMatch) return paths.librarySharedWithMe();
    if (sharedLibrariesMatch) return paths.librarySharedLibraries();
    if (profilesMatch) return paths.libraryUsersLibraries();
    if (searchMatch) return paths.librarySearch();
    return paths.library();
  }, [profilesMatch, searchMatch, sharedMatch, sharedLibrariesMatch]);

  const pageUrl = useMemo(() => {
    return addQueryParamsToUrl(baseUrl, queryParams);
  }, [baseUrl, queryParams]);

  // True only when viewing the root of shared libraries (no folder drilled into yet)
  const isSharedLibraryTopLevel =
    !!sharedLibrariesMatch && !queryParams.folder_id;

  const showDomainProfiles = profilesMatch && !queryParams.profile_id;

  // True only on /library (not /library/search, /library/shared,
  // /library/shared-libraries, /library/users). Source of truth for whether
  // "Duplicate and Move" is offered — the route, not just the API scope, so we
  // don't accidentally show it on screens that happen to share the 'owned' scope.
  const isMyLibraryRoute =
    !searchMatch && !sharedMatch && !sharedLibrariesMatch && !profilesMatch;

  // Selectors

  const currentUser = useSelector(selectUserProfile);
  const sortOptions = useSelector(selectMainSortOptions);
  const layoutOptions = useSelector(selectLayoutOptions);
  const batchMode = useSelector(selectIsManageMode);
  const localUploads = useSelector(selectLocalUploads);
  const selectedNodeIds = useSelector(selectSelectedNodeIds);
  const isEnterpriseUser = useSelector(selectIsEnterpriseUser);

  // Duplicate and Move is only meaningful from My Library when the user can
  // actually target a Shared Library.
  const showDuplicateAndMove = isMyLibraryRoute && isEnterpriseUser;

  // Folder duplication is only offered in My Library, Shared with me, and
  // Shared Library — never when browsing another user's library.
  const allowDuplicateFolder = !profilesMatch;

  const { data: domain } = useGetDomain();
  const domainUsers = useMemo(() => domain?.r.profiles ?? [], [domain]);

  // Get applications and themes for file upload functionality
  const applications = useSelector(selectApplications);
  const themesById = useSelector(selectThemesById);

  // States

  const [isAddPopOverOpen, setIsAddPopOverOpen] = useState(false);
  const [modalType, setModalType] = useState<LibraryPageModalType | null>(null);
  // "See in Library" deep-link target. Keyed on resourceId (globally unique;
  // content.id can collide across presentation/playlist/folder tables). Captured
  // once from the `highlight` URL param, then stripped from the URL so a refresh
  // or shared link doesn't re-trigger it. Cleared on the user's first interaction.
  const [highlightedResourceId, setHighlightedResourceId] = useState('');
  // Since we are fetching all presentations, playlists, and folders as Contents,
  // there might be duplicate IDs. However, resourceId is always unique.
  const [selectedResourceId, setSelectedResourceId] = useState('');
  // The right-side folder picker drives two flows: a plain "move" and
  // "duplicate_and_move" (duplicate the item into a Shared Library folder).
  // Tracking the mode here lets us pick the right destination scope, button
  // label and underlying mutation.
  const [sidebarMode, setSidebarMode] = useState<
    'move' | 'duplicate_and_move' | null
  >(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Out-of-tree unowned-content confirmation. When a copy preflight reports
  // unowned content we open a modal and park the copy on this resolver until
  // the user picks how to handle it (true/false) or cancels (null).
  const [unownedCopyResolver, setUnownedCopyResolver] = useState<
    ((choice: boolean | null) => void) | null
  >(null);

  // Queries

  const { data: onboardingData, isLoading: isOnboardingLoading } =
    useOnboardingStatus();
  const {
    data: libraryData,
    isLoading: libraryLoading,
    refetch,
  } = useLibrary(queryParams, {
    enabled: !showDomainProfiles,
  });
  const { mutateAsync: createFolder, isLoading: isCreateFolderLoading } =
    useCreateFolder(queryParams);
  const { mutateAsync: renameContent, isLoading: isRenameContentLoading } =
    useRenameContent();
  const { mutateAsync: moveAllItemsToFolder } = useMoveAllItemsToFolder();
  const { mutateAsync: deleteContent } = useDeleteContent();
  const { mutateAsync: copyContent } = useCopyContent();

  // Copy a content with the out-of-tree unowned-content confirmation step, and
  // drive the "Duplicating…" progress toast around it.
  //
  // The first request is a preflight (no `copyOutOfTreeUnowned`): if the server
  // reports unowned content it copies nothing and returns `hasOutOfTreeUnowned`.
  // In that case we hide the progress toast, open the modal, and once the user
  // confirms we re-show the toast and re-issue the copy with their choice.
  // Resolves with the final copy, or `null` if the user cancels the modal.
  const copyWithUnownedConfirmation = useCallback(
    async (
      content: Content,
      params: { name: string; folderId?: string },
      progressToast: { title: string; description: ReactNode },
    ) => {
      const showProgress = () => state.add(progressToast, { timeout: 0 });

      let progressKey = showProgress();
      let preflight: Awaited<ReturnType<typeof copyContent>>;
      try {
        preflight = await copyContent({ content, params });
      } catch (error) {
        state.close(progressKey);
        throw error;
      }

      // A preflight that needs the unowned-content decision comes back as
      // `{ hasOutOfTreeUnowned: true }`; a performed copy returns the resource
      // instead, which we hand straight back.
      if (!('hasOutOfTreeUnowned' in preflight)) {
        state.close(progressKey);
        return preflight;
      }

      // Unowned content found: hide the progress toast and ask the user how to
      // handle it.
      state.close(progressKey);
      const choice = await new Promise<boolean | null>((resolve) => {
        setUnownedCopyResolver(() => resolve);
      });
      // Close the dialog regardless of the choice.
      setUnownedCopyResolver(null);
      if (choice === null) return null;

      // Confirmed: bring the progress toast back and run the real copy.
      progressKey = showProgress();
      try {
        const confirmed = await copyContent({
          content,
          params: { ...params, copyOutOfTreeUnowned: choice },
        });
        // The confirmed request must perform the copy and return the resource.
        // A repeat preflight here means the backend ignored our explicit choice
        // — surface it as an error rather than silently no-op'ing.
        if ('hasOutOfTreeUnowned' in confirmed) {
          // `Error` is shadowed by an imported component in this file.
          throw new globalThis.Error(
            'Copy did not complete after confirming unowned content',
          );
        }
        return confirmed;
      } finally {
        state.close(progressKey);
      }
    },
    [copyContent, state],
  );

  // Memoizers
  const sortedLibraryData = useMemo(() => {
    if (!libraryData) return libraryData;

    const sorted = getSortedLibraryData(libraryData, sortOptions);

    // In "Shared With Me", hide content the current user owns — it already
    // appears in their own library and would otherwise show up twice. The
    // duplicate comes from the self-ACL we create when sharing a folder from
    // the personal library: shared_library requires the creator to have full
    // access to content inside it, so a self-ACL is written, which then
    // surfaces the same content back in the shared scope.
    if (sorted && queryParams.scope === 'shared' && currentUser) {
      return {
        ...sorted,
        data: sorted.data.filter(
          (content) => content.ownedBy.id !== currentUser.id,
        ),
      };
    }

    return sorted;
  }, [libraryData, sortOptions, queryParams.scope, currentUser]);

  const sortedDomainUsers = useMemo(() => {
    if (!sortOptions || !currentUser) return domainUsers;

    const isKeyName = sortOptions.property === 'name';

    return _.orderBy(
      domainUsers.filter((u) => u.id !== currentUser.id), // exclude current user (previously handled by selectOtherDomainProfiles)
      [(item) => item.name?.toLowerCase()],
      [isKeyName ? sortOptions.direction : 'asc'],
    );
  }, [domainUsers, sortOptions, currentUser]);

  const selectedContent = useMemo(
    () =>
      sortedLibraryData?.data.find(
        (content) => content.resourceId === selectedResourceId,
      ),
    [selectedResourceId, sortedLibraryData?.data],
  );

  const folderPaths = useMemo(
    () => sortedLibraryData?.folderPath || [],
    [sortedLibraryData],
  );

  const disableManageMultiple = useMemo(() => {
    if (!currentUser || !sortedLibraryData) return true;

    if (profilesMatch && !queryParams.profile_id) {
      return true;
    }

    return false;
  }, [currentUser, sortedLibraryData, profilesMatch, queryParams.profile_id]);

  const disableMoveContent = useMemo(() => {
    if (!currentUser || !sortedLibraryData) return true;

    const data = sortedLibraryData?.data || [];
    const filteredData = data.filter((content) =>
      selectedNodeIds.includes(`${content.resourceType}|${content.id}`),
    );

    // Shared libraries let ACL-granted collaborators move content; elsewhere only the owner can.
    const isMovable =
      queryParams.scope === 'shared_library'
        ? (content: Content) => canDeleteContent(currentUser, content)
        : (content: Content) => content.ownedBy.id === currentUser.id;

    return filteredData.some((content) => !isMovable(content)) ? true : false;
  }, [currentUser, sortedLibraryData, selectedNodeIds, queryParams.scope]);

  const disableBatchDelete = useMemo(() => {
    if (!currentUser || !sortedLibraryData) return true;

    const data = sortedLibraryData?.data || [];
    const filteredData = data.filter((content) =>
      selectedNodeIds.includes(`${content.resourceType}|${content.id}`),
    );

    return filteredData.some(
      (content) => !canDeleteContent(currentUser, content),
    )
      ? true
      : false;
  }, [currentUser, sortedLibraryData, selectedNodeIds]);

  const disabledFolderIds = useMemo(() => {
    const selectedFolderIdsFromNodeIds = selectedNodeIds
      .filter((nodeId) => nodeId.includes('folder'))
      .map((nodeId) => nodeId.split('|')[1])
      .filter(Boolean);

    const selectedFolderId =
      selectedContent?.resourceType === 'folder' ? [selectedContent.id] : [];

    return [...selectedFolderIdsFromNodeIds, ...selectedFolderId];
  }, [selectedContent?.id, selectedContent?.resourceType, selectedNodeIds]);

  // Variables
  const isLoading = libraryLoading || isOnboardingLoading;
  const libraryEmpty = sortedLibraryData?.data.length === 0;
  const selectedProfileId = queryParams.profile_id;
  const selectedFolderId = queryParams.folder_id;
  // "See in Library" back-to-playlist boundary. When the user arrives via the
  // playlist action, the landing folder is the session boundary: the back
  // control returns to the playlist while we sit on that folder (at the root
  // both are empty strings, which still matches), and reverts to normal
  // parent-folder navigation once the user drills deeper.
  const isBackToPlaylistBoundary =
    !!queryParams.back_to && selectedFolderId === queryParams.back_to_folder;
  const previousFolderPath = folderPaths[folderPaths.length - 2];
  const previousFolderId = previousFolderPath?.id || '';
  const isLibraryOnboardingCompleted = !!onboardingData?.some(
    (item) => item.name === 'library',
  );

  // Callbacks
  const navigateWithQuery = useCallback(
    (pathname: string, queryParams: LibraryQueryParams) => {
      history.push({
        pathname: pathname,
        search: getQueryString(queryParams),
      });
    },
    [history],
  );

  const setSelectedNodeIds = useCallback(
    (nodeIds: string[]) => {
      dispatch(actions.setSelectedNodeIds(nodeIds));
    },
    [dispatch],
  );

  const toggleNodeId = useCallback(
    (content: Content) => {
      const nodeId = `${content.resourceType}|${content.id}`;
      setSelectedNodeIds(xor(selectedNodeIds, [nodeId]));
    },
    [setSelectedNodeIds, selectedNodeIds],
  );

  // Capture the "See in Library" highlight target once on load, then strip the
  // `highlight` param from the URL (kept out of queryParams so it never leaks
  // back into constructed page URLs). The highlight persists in state until the
  // first interaction clears it — never on a timer.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const highlight = params.get('highlight');
    if (!highlight) return;
    setHighlightedResourceId(highlight);
    params.delete('highlight');
    // Spread the current location so stripping `highlight` only rewrites the
    // search string and preserves any existing hash/state on this entry.
    history.replace({
      ...location,
      search: params.toString(),
    });
    // Runs once for the deep-link load; intentionally not reactive to later
    // URL changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearHighlight = useCallback(() => {
    setHighlightedResourceId('');
  }, []);

  const onToggleAddPopOver = useCallback((open: boolean) => {
    // Track only when opening the popover
    if (open) {
      analytics.trackLibraryAddButtonClicked({
        pageName: 'library',
        entryPoint: 'add_button',
      });
    }
    setIsAddPopOverOpen(open);
  }, []);

  const onAddPopOverAction = useCallback(
    (type: LibraryPageModalType) => {
      if (type === 'folder') {
        analytics.trackLibraryAddButtonClicked({
          pageName: 'library',
          entryPoint: 'add_folder',
        });
        analytics.trackLibraryItemSelected({ itemType: 'folder' });
      }
      onToggleAddPopOver(false);
      setModalType(type);
    },
    [onToggleAddPopOver],
  );

  const onCloseModal = useCallback(() => {
    onToggleAddPopOver(false);
    setModalType(null);
    setSidebarMode(null);
  }, [onToggleAddPopOver]);

  const onNewFolder = useCallback(
    async (folderName: string) => {
      try {
        const payload: CreateFolder = {
          name: folderName,
          description: null,
          // 'shared_library' is not yet in the API client's FolderType union — cast required until the package is updated
          type: (isSharedLibraryTopLevel
            ? 'shared_library'
            : 'library') as CreateFolder['type'],
        };

        const folder = await createFolder(payload);

        if (isSharedLibraryTopLevel) {
          analytics.trackSharedLibraryCreateSuccess({
            folderId: folder.id,
            name: folder.name,
          });
        }

        onCloseModal();

        state.add({
          title: 'Folder Added',
          description: 'New folder added successfully',
        });

        const updatedQuery = {
          ...queryParams,
          folder_id: folder.id,
        };

        navigateWithQuery(baseUrl, updatedQuery);
      } catch (error) {
        state.add({
          title: 'Something went wrong',
          description: 'Please try again',
          variant: 'caution',
        });
      }
    },
    [
      baseUrl,
      createFolder,
      isSharedLibraryTopLevel,
      navigateWithQuery,
      onCloseModal,
      queryParams,
      state,
    ],
  );

  const onNewPlaylist = useCallback(() => {
    analytics.trackLibraryAddButtonClicked({
      pageName: 'library',
      entryPoint: 'add_playlist',
    });
    analytics.trackLibraryItemSelected({ itemType: 'playlist' });
    history.push(
      paths.newPlaylist({
        playlistId: createNewId(),
        folderId: selectedFolderId,
        // We don't want to come back to the app selector page,
        // forward the backTo to the next page.
        backTo: pageUrl,
        saveTo: pageUrl,
        backToLabel: 'Back to Library',
        scope: sharedLibrariesMatch ? 'shared_library' : undefined,
      }),
    );
  }, [history, pageUrl, selectedFolderId, sharedLibrariesMatch]);

  const onCreateNewPlaylistFromContent = useCallback(
    (content: Content) => {
      const playlistId = createNewId();

      const playlistItem: {
        id: string;
        presentationId: string | null;
        playlistId: string | null;
        presentation: null;
        playlist: null;
      } = {
        id: createNewId(),
        presentationId:
          content.resourceType === 'presentation' ? content.id : null,
        playlistId: content.resourceType === 'playlist' ? content.id : null,
        presentation: null,
        playlist: null,
      };

      dispatch(
        addContentPageActions.setItemsToAdd({
          selectionId: 'PlaylistPage',
          items: [playlistItem],
        }),
      );

      history.push(
        paths.newPlaylist({
          playlistId,
          playlistName: content.name,
          folderId: selectedFolderId,
          backTo: pageUrl,
          saveTo: pageUrl,
          backToLabel: 'Back to Library',
          scope: sharedLibrariesMatch ? 'shared_library' : undefined,
        }),
      );
    },
    [dispatch, history, pageUrl, selectedFolderId, sharedLibrariesMatch],
  );

  const onAddToPlaylistClick = useCallback(
    (content: Content) => {
      history.push(
        paths.presentations({
          backTo: pageUrl,
          saveTo: pageUrl,
          addToLabel: content.name,
          singleSelect: true,
          playlistOnly: true,
          addContentId: content.id,
          addContentType: content.resourceType,
        }),
      );
    },
    [history, pageUrl],
  );

  const onNewPresentation = useCallback(() => {
    const targetPath = paths.applications({
      backTo: pageUrl,
      folderId: selectedFolderId,
    });

    analytics.trackLibraryAddButtonClicked({
      pageName: 'library',
      entryPoint: 'add_presentation',
    });
    analytics.trackLibraryItemSelected({ itemType: 'presentation' });
    history.push(targetPath);
  }, [history, pageUrl, selectedFolderId]);

  // File upload related functions
  const createPresentationsForFiles = useCallback(
    async (fileList: FileList) => {
      if (!currentUser) {
        return;
      }

      const files = Array.from(fileList);

      // Check if applications data is available and load if necessary
      if (!applications || applications.length === 0) {
        dispatch(applicationsPageActions.loadApplicationsPage());

        // Wait for applications to be loaded
        let attempts = 0;
        const maxAttempts = 20;
        while (
          (!applications || applications.length === 0) &&
          attempts < maxAttempts
        ) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          attempts++;

          // Re-check applications from selector in case it's updated
          if (applications && applications.length > 0) {
            break;
          }
        }

        if (!applications || applications.length === 0) {
          return;
        }
      }

      const picturesApp = applications?.find(
        (app: any) => app.id === picturesAppId,
      );

      if (!picturesApp) {
        return;
      }

      const webpFiles = files.filter((f) => f.type === 'image/webp');
      if (webpFiles.length > 0) {
        state.add({
          title: 'Unsupported file format',
          description:
            'WebP format is not supported. Please upload one of the following formats: .svg, JPEG (.jpg/.jpeg), .png, .gif.',
          variant: 'caution',
        });
      }

      const presentationsToSave: Array<Promise<P.Presentation>> = [];

      files.forEach((file) => {
        // Check if file is an image and not webp
        if (!file.type.startsWith('image/') || file.type === 'image/webp') {
          return;
        }

        const fileUploadProp =
          picturesApp.currentAppVersion.presentationProperties.find(
            (p: any) => p.type === 'file',
          );

        if (!fileUploadProp) {
          return;
        }

        // Create a default presentation for the file.
        const presentation = createPresentationFromAppVersion(
          picturesApp.currentAppVersion,
          currentUser.id,
          getDefaultThemeForUser(currentUser, themesById),
          domain?.defaultPresentationDuration ?? null,
        );
        presentation.name = file.name;
        presentation.applicationVariables[fileUploadProp.name] = {
          filename: file.name,
          'content-type': file.type,
          'content-length': file.size,
        };

        // We currently don't support file uploads that aren't at the
        // root level (ie. file upload inside an array input) but we
        // need to fake the path anyways.
        const path = ['applicationVariables', fileUploadProp.name];
        presentationsToSave.push(
          new Promise((resolve) =>
            dispatch(
              presActions.savePresentation(presentation, {
                fileUploads: [
                  { path, file, localUrl: URL.createObjectURL(file) },
                ],
                folderId: selectedFolderId,
                onSave: (savedPresentation) => resolve(savedPresentation),
              }),
            ),
          ),
        );
      });

      const savedPresentations = await Promise.all(presentationsToSave);

      if (savedPresentations.length > 0) {
        state.add({
          title: 'Presentations Created',
          description: `${savedPresentations.length} presentations created successfully`,
        });
      }

      // Refresh the library data
      refetch();
    },
    [
      currentUser,
      domain,
      applications,
      themesById,
      selectedFolderId,
      dispatch,
      state,
      refetch,
    ],
  );

  const handleFileInputChange = useCallback(() => {
    if (fileInput.current && fileInput.current.files) {
      createPresentationsForFiles(fileInput.current.files);
    }
  }, [createPresentationsForFiles]);

  const openFileBrowser = useCallback((mimetypes: string[]) => {
    if (fileInput.current) {
      fileInput.current.accept = mimetypes.join(',');
      fileInput.current.click();
    }
  }, []);

  const onPresentationCardClick = useCallback(
    (applicationId: string) => {
      dispatch(presentationPageActions.clearUnsavedPresentation(applicationId));

      history.push(
        paths.newPresentation({
          applicationId,
          folderId: queryParams.folder_id,
          backTo: pageUrl,
          saveTo: pageUrl,
          backToLabel: 'Back to Library',
        }),
      );
    },
    [dispatch, history, pageUrl, queryParams.folder_id],
  );

  const onBackToParentFolder = useCallback(() => {
    // At the "See in Library" boundary, back returns to the playlist the user
    // came from instead of walking up the folder tree.
    if (isBackToPlaylistBoundary) {
      history.push(queryParams.back_to);
      return;
    }

    const nextQueryParams = {
      ...queryParams,
    };

    if (selectedFolderId) {
      nextQueryParams.folder_id = previousFolderId;
    } else if (selectedProfileId) {
      nextQueryParams.profile_id = '';
    }

    navigateWithQuery(baseUrl, nextQueryParams);
  }, [
    baseUrl,
    history,
    isBackToPlaylistBoundary,
    navigateWithQuery,
    previousFolderId,
    queryParams,
    selectedFolderId,
    selectedProfileId,
  ]);

  const onSelectUser = useCallback(
    (profileId: string) => {
      const nextQueryParams = {
        ...queryParams,
        profile_id: profileId,
      };
      navigateWithQuery(baseUrl, nextQueryParams);
    },
    [baseUrl, navigateWithQuery, queryParams],
  );

  const onOpenFolder = useCallback(
    (folderId: string) => {
      const nextQueryParams = {
        ...queryParams,
        folder_id: folderId,
      };
      navigateWithQuery(baseUrl, nextQueryParams);
    },
    [baseUrl, navigateWithQuery, queryParams],
  );

  const onEditPlaylist = useCallback(
    (playlistId: string) => {
      history.push(
        paths.editPlaylist(playlistId, {
          backTo: pageUrl,
          backToLabel: 'Back To Library',
          saveTo: pageUrl,
          folderId: queryParams.folder_id,
          // Propagate scope so the add-content modal opens in the shared libraries section
          scope: sharedLibrariesMatch ? 'shared_library' : undefined,
        }),
      );
    },
    [history, pageUrl, queryParams.folder_id, sharedLibrariesMatch],
  );

  const onEditPresentation = useCallback(
    (presentationId: string) => {
      dispatch(
        presentationPageActions.clearUnsavedPresentation(presentationId),
      );
      history.push(
        paths.editPresentation(presentationId, {
          backTo: pageUrl,
          backToLabel: 'Back to Library',
          saveTo: pageUrl,
        }),
      );
    },
    [pageUrl, dispatch, history],
  );

  const onSelectContent = useCallback(
    (content: Content) => {
      if (batchMode) {
        return toggleNodeId(content);
      }

      switch (content.resourceType) {
        case 'folder':
          return onOpenFolder(content.id);
        case 'playlist':
          return onEditPlaylist(content.id);
        case 'presentation':
          return onEditPresentation(content.id);
        default:
          return;
      }
    },
    [batchMode, onEditPlaylist, onEditPresentation, onOpenFolder, toggleNodeId],
  );

  const onContentCardMoreActions = useCallback((resourceId: string) => {
    setSelectedResourceId(resourceId);
  }, []);

  const onContentCardAction = useCallback((type: LibraryPageModalType) => {
    if (type === 'move') {
      setSidebarMode('move');
    } else if (type === 'duplicate_and_move') {
      setSidebarMode('duplicate_and_move');
    } else {
      setModalType(type);
    }
  }, []);

  const onMoveNodeIdsToFolder = useCallback(
    async (nodeIds: string[], folderId: string | null) => {
      const { presentationIds, playlistIds, folderIds } =
        getResourceIdsFromNodeIds(nodeIds);

      try {
        await moveAllItemsToFolder({
          presentationIds,
          playlistIds,
          folderIds,
          parentFolderId: folderId || null,
        });

        onCloseModal();

        dispatch(actions.clearSelectedNodeIds());
        dispatch(libraryTreeActions.clearSelectedNodeIds());

        state.add({
          title: 'Contents Moved',
          description: `${selectedNodeIds.length} contents moved successfully`,
        });
      } catch (error) {
        state.add({
          title: 'Something went wrong',
          description: 'Please try again',
          variant: 'caution',
        });
      }
    },
    [selectedNodeIds, moveAllItemsToFolder, onCloseModal, dispatch, state],
  );

  const onMoveBatchContent = useCallback(
    (folderId: string | null) => {
      return onMoveNodeIdsToFolder(selectedNodeIds, folderId);
    },
    [onMoveNodeIdsToFolder, selectedNodeIds],
  );

  const onDeleteNodeIds = useCallback(async () => {
    setIsDeleting(true);

    const { presentationIds, playlistIds, folderIds } =
      getResourceIdsFromNodeIds(selectedNodeIds);

    try {
      await Promise.all([
        ...presentationIds.map((id) =>
          deleteContent({ id, resourceType: 'presentation' }),
        ),
        ...playlistIds.map((id) =>
          deleteContent({ id, resourceType: 'playlist' }),
        ),
        ...folderIds.map((id) => deleteContent({ id, resourceType: 'folder' })),
      ]);

      onCloseModal();

      dispatch(actions.clearSelectedNodeIds());
      dispatch(libraryTreeActions.clearSelectedNodeIds());

      state.add({
        title: 'Contents Deleted',
        description: `${selectedNodeIds.length} contents deleted successfully`,
      });
    } catch (error) {
      state.add({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'caution',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [selectedNodeIds, onCloseModal, dispatch, state, deleteContent]);

  const onRenameContent = useCallback(
    async (name: string) => {
      if (!selectedContent) return;

      try {
        await renameContent({
          id: selectedContent.id,
          params: { name, resourceType: selectedContent.resourceType },
        });

        onCloseModal();

        state.add({
          title: `${capitalize(selectedContent.resourceType)} renamed`,
          description: `${capitalize(
            selectedContent.resourceType,
          )} renamed successfully`,
        });
      } catch (error) {
        state.add({
          title: 'Something went wrong',
          description: 'Please try again',
          variant: 'caution',
        });
      }
    },
    [selectedContent, renameContent, onCloseModal, state],
  );

  const onMoveContent = useCallback(
    async (folderId: string | null) => {
      if (!selectedContent) return;

      const resourceType = selectedContent?.resourceType;

      try {
        await moveAllItemsToFolder({
          presentationIds:
            resourceType === 'presentation' ? [selectedContent.id] : [],
          playlistIds: resourceType === 'playlist' ? [selectedContent.id] : [],
          folderIds: resourceType === 'folder' ? [selectedContent.id] : [],
          parentFolderId: folderId || null,
        });

        onCloseModal();
        setSelectedResourceId('');

        state.add({
          title: 'Content Moved',
          description: `${selectedContent.name} moved successfully`,
        });
      } catch (error) {
        state.add({
          title: 'Something went wrong',
          description: 'Please try again',
          variant: 'caution',
        });
      }
    },
    [selectedContent, moveAllItemsToFolder, onCloseModal, state],
  );

  // Duplicate a folder via the new POST /v3/folders/:id/actions/copy endpoint.
  // The progress toast stays up until the request resolves; we then swap it
  // for a success or failure toast so the user sees one continuous status.
  const onDuplicateFolder = useCallback(
    async (folder: Content) => {
      if (folder.resourceType !== 'folder') return;

      const name = createDuplicateContentName(
        folder.name,
        libraryData?.data || [],
      );

      try {
        const newFolder = await copyWithUnownedConfirmation(
          folder,
          { name },
          {
            title: 'Duplicating folder',
            description: (
              <div className="flex items-center gap-2">
                <Spinner size="small" />
                <span>Duplicating {folder.name}...</span>
              </div>
            ),
          },
        );
        // User dismissed the unowned-content modal — abort quietly.
        if (!newFolder) return;

        // Where the duplicate actually lands (mirrors the backend's behaviour):
        // - In Shared Library where the user can edit the source folder, the
        //   copy stays in the Shared Library scope.
        // - Everywhere else (My Library, User Libraries, Shared with Me, or
        //   Shared Library without edit access) the copy lands in My Library.
        const canEdit = currentUser
          ? canEditContent(currentUser, folder)
          : false;
        const targetBaseUrl =
          !!sharedLibrariesMatch && canEdit
            ? paths.librarySharedLibraries()
            : paths.library();
        const targetUrl = addQueryParamsToUrl(targetBaseUrl, {
          folder_id: newFolder.id,
        });

        state.add({
          title: 'Folder duplicated',
          description: (
            <div
              className={cn(
                'flex flex-col gap-1',
                newFolder.supportingContentFolderCreated &&
                  'toast-dynamic-width',
              )}
            >
              <span>
                Folder duplicated successfully.
                {newFolder.supportingContentFolderCreated &&
                  SUPPORTING_CONTENT_MESSAGE}
              </span>
              <span>
                <Link onPress={() => history.push(targetUrl)}>
                  Go to folder
                </Link>
              </span>
            </div>
          ),
        });
      } catch {
        state.add({
          title: 'Something went wrong',
          description: 'Please try again',
          variant: 'caution',
        });
      }
    },
    [
      copyWithUnownedConfirmation,
      libraryData,
      state,
      currentUser,
      sharedLibrariesMatch,
      history,
    ],
  );

  // Plain duplicate for non-folder content. Hoisted from
  // ContentCardActionButtonGroup so the page-level handler can drive a single
  // progress/success/error toast for all duplicate flows.
  const onDuplicateContent = useCallback(
    async (content: Content) => {
      if (content.resourceType === 'folder' || !currentUser) return;

      const canEdit = canEditContent(currentUser, content);
      const isMyLibraryScope =
        queryParams.scope === 'owned' && !queryParams.profile_id;
      const isSharedLibraryScope = queryParams.scope === 'shared_library';
      // Mirrors the legacy card-level rule: write into the current folder only
      // when in My Library, or in a Shared Library the user can edit. Otherwise
      // fall back to the user's root library.
      const canDuplicateToCurrentFolder =
        isMyLibraryScope || (isSharedLibraryScope && canEdit);

      try {
        const result = await copyWithUnownedConfirmation(
          content,
          {
            name: createDuplicateContentName(
              content.name,
              libraryData?.data || [],
            ),
            folderId: canDuplicateToCurrentFolder
              ? queryParams.folder_id || undefined
              : undefined,
          },
          {
            title: `Duplicating ${content.resourceType}`,
            description: (
              <div className="flex items-center gap-2">
                <Spinner size="small" />
                <span>Duplicating {content.name}...</span>
              </div>
            ),
          },
        );
        // User dismissed the unowned-content modal — abort quietly.
        if (!result) return;

        state.add({
          title: `${capitalize(content.resourceType)} duplicated`,
          description: result.supportingContentFolderCreated ? (
            <span className="toast-dynamic-width">
              {capitalize(content.resourceType)} duplicated successfully.
              {SUPPORTING_CONTENT_MESSAGE}
            </span>
          ) : (
            `${capitalize(content.resourceType)} duplicated successfully`
          ),
        });
      } catch {
        state.add({
          title: 'Something went wrong',
          description: 'Please try again',
          variant: 'caution',
        });
      }
    },
    [
      copyWithUnownedConfirmation,
      libraryData,
      state,
      currentUser,
      queryParams.scope,
      queryParams.profile_id,
      queryParams.folder_id,
    ],
  );

  // "Copy to Shared Library": create a copy of the selected item and place it
  // in a target Shared Library folder. The user picks the destination via the
  // MoveContent sidebar opened in 'duplicate_and_move' mode. Shows a progress
  // toast that swaps into a success toast with a "Go to folder" link, or an
  // error toast on failure.
  const onDuplicateAndMove = useCallback(
    async (targetFolderId: string | null, sourceOverride?: Content) => {
      const source = sourceOverride ?? selectedContent;
      if (!source) return;
      // Shared library root isn't a valid destination; the MoveContent tree
      // only renders the Move button on nested folders for shared_library
      // scope, so we never actually expect a null here.
      if (!targetFolderId) return;

      const duplicateName = createDuplicateContentName(
        source.name,
        libraryData?.data || [],
      );

      try {
        const result = await copyWithUnownedConfirmation(
          source,
          {
            name: duplicateName,
            folderId: targetFolderId,
          },
          {
            title: 'Copying to Shared Library',
            description: (
              <div className="flex items-center gap-2">
                <Spinner size="small" />
                <span>Duplicating {source.name}...</span>
              </div>
            ),
          },
        );
        // User dismissed the unowned-content modal — abort quietly.
        if (!result) return;

        const targetUrl = addQueryParamsToUrl(paths.librarySharedLibraries(), {
          folder_id: targetFolderId,
        });

        state.add({
          title: `${capitalize(source.resourceType)} copied and moved`,
          description: (
            <div
              className={cn(
                'flex flex-col gap-1',
                result.supportingContentFolderCreated && 'toast-dynamic-width',
              )}
            >
              <span>
                Duplicated to shared library.
                {result.supportingContentFolderCreated &&
                  SUPPORTING_CONTENT_MESSAGE}
              </span>
              <span>
                <Link onPress={() => history.push(targetUrl)}>
                  Go to folder
                </Link>
              </span>
            </div>
          ),
        });

        onCloseModal();
        setSelectedResourceId('');
      } catch {
        state.add({
          title: 'Something went wrong',
          description: 'Please try again',
          variant: 'caution',
        });
      }
    },
    [
      selectedContent,
      libraryData,
      state,
      copyWithUnownedConfirmation,
      history,
      onCloseModal,
    ],
  );

  const fetchPresentations = useCallback(async (ids: string[]) => {
    try {
      const presentations = await miraClient.getPresentations({ ids });
      return presentations;
    } catch (error) {
      console.error(error);
    }
  }, []);

  // Effects
  useEffect(() => {
    // Reset batchmode when we switch between pages to prevent items getting multiselected between pages
    // Currently My Library, Shared with Me, User Libraries are same page so we need to make sure it is cleared
    dispatch(actions.setManageMode(false));
    dispatch(actions.clearSelectedNodeIds());
    dispatch(libraryTreeActions.clearSelectedNodeIds());

    if (baseUrl === paths.libraryUsersLibraries()) {
      analytics.trackSharedContentViewed({ pageName: 'user_libraries' });
    } else if (baseUrl === paths.librarySharedWithMe()) {
      analytics.trackSharedContentViewed({ pageName: 'shared_with_me' });
    }
  }, [dispatch, baseUrl, selectedProfileId]);

  useEffect(() => {
    // Cancel manage multiple when navigating back to the top-level shared library page
    if (isSharedLibraryTopLevel) {
      dispatch(actions.setManageMode(false));
      dispatch(actions.clearSelectedNodeIds());
      dispatch(libraryTreeActions.clearSelectedNodeIds());
    }
  }, [dispatch, isSharedLibraryTopLevel]);

  useEffect(() => {
    if (!applications || applications.length === 0) {
      dispatch(applicationsPageActions.loadApplicationsPage());
    }
  }, [dispatch, applications]);

  // Add warning when trying to reload page during file uploads
  useEffect(() => {
    const hasUploadingItems = Object.values(localUploads).some(
      (item) => item.isUploading,
    );

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUploadingItems) {
        e.preventDefault();
        // "Changes you made may not be saved." will show in the browser. The message is not customizable.
        return 'Changes you made may not be saved.';
      }
    };

    if (hasUploadingItems) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [localUploads]);

  useEffect(() => {
    const hasUploadingItems = Object.values(localUploads).some(
      (item) => item.isUploading,
    );

    if (hasUploadingItems) {
      refetch();
    }
  }, [localUploads, refetch]);

  useEffect(() => {
    if (!libraryData) return;

    let uploadingPresentationIds = libraryData?.data
      .filter(
        (content) =>
          content.resourceType === 'presentation' &&
          content.presentationData?.fileUploads?.some(
            (fileUpload) =>
              !fileUpload?.completedSteps?.some(
                (step) => step.stepName === 'upload_completed',
              ),
          ),
      )
      .map((content) => content.id);

    if (uploadingPresentationIds.length === 0) {
      return;
    }

    let isFetching = false;

    const interval = setInterval(async () => {
      if (isFetching || uploadingPresentationIds.length === 0) return;
      isFetching = true;

      const updatedPresentations = await fetchPresentations(
        uploadingPresentationIds,
      );

      uploadingPresentationIds = uploadingPresentationIds.filter((id) => {
        const presentation = updatedPresentations?.find((p) => p.id === id);
        const isStillUploading = presentation?.fileUploads?.some(
          (fileUpload) =>
            !fileUpload?.completedSteps?.some(
              (step) => step.stepName === 'upload_completed',
            ),
        );
        return isStillUploading;
      });

      isFetching = false;

      if (uploadingPresentationIds.length === 0) {
        refetch();
        clearInterval(interval);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [libraryData, fetchPresentations, refetch]);

  // Renders

  const pageTitle = useMemo(() => {
    // The boundary back control shows even at the library root (no folder), so
    // it isn't gated on a selected folder/profile like the normal one.
    const showBackButton =
      selectedProfileId || selectedFolderId || isBackToPlaylistBoundary;

    let title: string = 'My Library';
    if (sharedMatch) {
      title = 'Shared With Me';
    }
    if (sharedLibrariesMatch) {
      title = 'Shared Libraries';
    }
    if (searchMatch) {
      title = 'Search Results';
    }
    if (profilesMatch) {
      if (selectedProfileId) {
        const selectedProfile = sortedDomainUsers.find(
          (profile) => profile.id === selectedProfileId,
        );
        title = `${selectedProfile?.name}'s Library`;
      }
      title = 'User Libraries';
    }
    if (selectedFolderId) {
      title = folderPaths[folderPaths.length - 1]?.name || '';
    }

    return (
      <div className="flex items-center mb-4">
        {showBackButton && (
          <button className="p-2" onClick={onBackToParentFolder}>
            {BACK_BUTTON_ICON}
          </button>
        )}
        <div className="text-2xl font-bold text-black">{title}</div>
      </div>
    );
  }, [
    selectedProfileId,
    selectedFolderId,
    isBackToPlaylistBoundary,
    sharedMatch,
    sharedLibrariesMatch,
    searchMatch,
    profilesMatch,
    onBackToParentFolder,
    sortedDomainUsers,
    folderPaths,
  ]);

  const pageContent: ReactNode = useMemo(() => {
    if (showDomainProfiles) {
      return (
        <DomainProfiles
          layout={layoutOptions}
          domainProfiles={sortedDomainUsers || []}
          onClick={onSelectUser}
        />
      );
    }

    if (queryParams.search && libraryEmpty) {
      return (
        <div className="w-full md:my-20">
          <Error
            title="No results"
            message="There’s no data for the selected filters, please try different search parameter"
          />
        </div>
      );
    }

    if (libraryEmpty) {
      return (
        <EmptyLibrary
          onPresentationCardClick={onPresentationCardClick}
          // In shared library context, the primary CTA creates a folder rather than a new presentation
          onNewPresentation={
            isSharedLibraryTopLevel
              ? () => onAddPopOverAction('folder')
              : onNewPresentation
          }
          onFileUpload={() => {
            openFileBrowser([
              'image/jpeg',
              'image/png',
              'image/gif',
              'image/svg+xml',
            ]);
          }}
          isSharedLibrary={isSharedLibraryTopLevel}
          createButtonLabel={
            isSharedLibraryTopLevel ? 'Create a Shared Library' : undefined
          }
        />
      );
    }

    if (layoutOptions === 'list') {
      return (
        <ListViewLayout
          contents={sortedLibraryData?.data || []}
          batchMode={batchMode}
          hideMoveOption={isSharedLibraryTopLevel}
          libraryQuery={queryParams as typeof DEFAULT_QUERY}
          // Shared library folders label the owner column 'Created by' to match UX intent
          folderOwnerLabel={sharedLibrariesMatch ? 'Created by' : undefined}
          isTopLevelSharedLibraryFolder={isSharedLibraryTopLevel}
          // Share is only exposed at the top level; hide it once inside a nested folder
          hideShare={!!sharedLibrariesMatch && !!queryParams.folder_id}
          selectedNodeIds={selectedNodeIds}
          highlightedResourceId={highlightedResourceId}
          onClick={onSelectContent}
          onButtonGroupClick={onContentCardMoreActions}
          onButtonGroupItemClick={onContentCardAction}
          onClose={onCloseModal}
          onCreateNewPlaylist={onCreateNewPlaylistFromContent}
          onAddToPlaylist={onAddToPlaylistClick}
          onDuplicateFolder={
            allowDuplicateFolder ? onDuplicateFolder : undefined
          }
          onDuplicateContent={onDuplicateContent}
          showDuplicateAndMove={showDuplicateAndMove}
        />
      );
    }

    return (
      <GridViewLayout
        contents={sortedLibraryData?.data || []}
        showOwner={baseUrl !== paths.library()}
        hideMoveOption={isSharedLibraryTopLevel}
        libraryQuery={queryParams as typeof DEFAULT_QUERY}
        // Shared library folders label the owner column 'Created by' to match UX intent
        folderOwnerLabel={sharedLibrariesMatch ? 'Created by' : undefined}
        isTopLevelSharedLibraryFolder={isSharedLibraryTopLevel}
        // Share is only exposed at the top level; hide it once inside a nested folder
        hideShare={!!sharedLibrariesMatch && !!queryParams.folder_id}
        batchMode={batchMode}
        selectedNodeIds={selectedNodeIds}
        highlightedResourceId={highlightedResourceId}
        onClick={onSelectContent}
        onButtonGroupClick={onContentCardMoreActions}
        onButtonGroupItemClick={onContentCardAction}
        onClose={onCloseModal}
        isMoveable={queryParams.scope === 'owned' && !queryParams.profile_id}
        folderId={selectedFolderId}
        onMove={onMoveNodeIdsToFolder}
        onSelectNodeIds={setSelectedNodeIds}
        onToggleNodeId={toggleNodeId}
        onCreateNewPlaylist={onCreateNewPlaylistFromContent}
        onAddToPlaylist={onAddToPlaylistClick}
        onDuplicateFolder={allowDuplicateFolder ? onDuplicateFolder : undefined}
        onDuplicateContent={onDuplicateContent}
        showDuplicateAndMove={showDuplicateAndMove}
      />
    );
  }, [
    baseUrl,
    batchMode,
    highlightedResourceId,
    isSharedLibraryTopLevel,
    layoutOptions,
    libraryEmpty,
    openFileBrowser,
    onAddPopOverAction,
    onCloseModal,
    onContentCardAction,
    onContentCardMoreActions,
    onAddToPlaylistClick,
    onCreateNewPlaylistFromContent,
    onMoveNodeIdsToFolder,
    onNewPresentation,
    onPresentationCardClick,
    onSelectContent,
    onSelectUser,
    queryParams,
    selectedFolderId,
    selectedNodeIds,
    setSelectedNodeIds,
    sharedLibrariesMatch,
    showDomainProfiles,
    sortedDomainUsers,
    sortedLibraryData?.data,
    toggleNodeId,
    onDuplicateFolder,
    onDuplicateContent,
    showDuplicateAndMove,
    allowDuplicateFolder,
  ]);

  const pageModal: ReactNode = useMemo(() => {
    if (!selectedContent && modalType !== 'folder') return null;

    switch (modalType) {
      case 'folder':
        return (
          <CreateFolderDialog
            isLoading={isCreateFolderLoading}
            isOpen={true}
            // Override default dialog copy when creating a top-level shared library
            title={isSharedLibraryTopLevel ? 'New Shared Library' : undefined}
            placeholder={
              isSharedLibraryTopLevel ? 'Enter shared library name' : undefined
            }
            onSubmit={onNewFolder}
            onClose={onCloseModal}
          />
        );
      case 'share':
        if (!selectedContent) return null;
        return (
          <ShareDialog
            content={selectedContent}
            isOpen={true}
            onClose={onCloseModal}
            // Enables restricted sharing behaviour (fixed grant level, read-only selector) for shared library roots
            isTopLevelSharedLibraryFolder={
              !!sharedLibrariesMatch &&
              !queryParams.folder_id &&
              selectedContent.resourceType === 'folder'
            }
          />
        );
      case 'rename':
        if (!selectedContent) return null;
        return (
          <RenameContentDialog
            content={selectedContent}
            isLoading={isRenameContentLoading}
            isOpen={true}
            onSubmit={onRenameContent}
            onClose={onCloseModal}
          />
        );
      case 'delete':
        if (!selectedContent) return null;
        return (
          <DeleteContentDialog
            content={selectedContent}
            isOpen={true}
            onSubmit={onCloseModal}
            onClose={onCloseModal}
          />
        );
      default:
        return null;
    }
  }, [
    selectedContent,
    modalType,
    isCreateFolderLoading,
    isSharedLibraryTopLevel,
    onNewFolder,
    onCloseModal,
    isRenameContentLoading,
    onRenameContent,
    queryParams.folder_id,
    sharedLibrariesMatch,
  ]);

  if (isLoading) {
    return (
      <Page title="Library">
        <PageLayout
          title={
            <PageTitle
              className="flex justify-between align-top"
              title="Libraries"
              subTitle="View and manage your library and shared content"
              button={
                <LibraryPageActionButton
                  isOpen={isAddPopOverOpen}
                  onClick={onToggleAddPopOver}
                  isDisabled={true}
                  onFolderClick={() => {
                    onAddPopOverAction('folder');
                  }}
                  onPresentationClick={() => {
                    setIsAddPopOverOpen(false);
                    onNewPresentation();
                  }}
                  onPlaylistClick={() => {
                    setIsAddPopOverOpen(false);
                    onNewPlaylist();
                  }}
                />
              }
            />
          }
        >
          <div className="mb-1.5">
            <LibraryPageActionBar
              onManageMode={() => {}}
              onMove={() => {}}
              onCreate={() => {}}
              onDelete={() => {}}
            />
          </div>
          <div className="flex min-h-[300px] justify-center items-center md:min-h-[503px]">
            <Spinner size="large" />
          </div>
        </PageLayout>
      </Page>
    );
  }

  return (
    <Page title="Library">
      <PageLayout
        title={
          <PageTitle
            className="flex justify-between align-top"
            title="Libraries"
            subTitle="View and manage your library and shared content"
            button={
              <LibraryPageActionButton
                isOpen={isAddPopOverOpen}
                // Button is enabled on /library and /library/shared-libraries, disabled on all other library sub-routes
                isDisabled={
                  baseUrl !== paths.library() && !sharedLibrariesMatch
                }
                // Label and action of the primary popover item change in shared library context
                buttonLabel={
                  isSharedLibraryTopLevel ? 'Shared Library' : 'Presentation'
                }
                isSharedLibraryTopLevel={isSharedLibraryTopLevel}
                onClick={onToggleAddPopOver}
                onFolderClick={() => onAddPopOverAction('folder')}
                onPresentationClick={() => {
                  // At the shared libraries root, always create a folder (shared library) instead of a presentation
                  if (isSharedLibraryTopLevel) {
                    onAddPopOverAction('folder');
                  } else {
                    setIsAddPopOverOpen(false);
                    onNewPresentation();
                  }
                }}
                onPlaylistClick={() => {
                  setIsAddPopOverOpen(false);
                  onNewPlaylist();
                }}
              />
            }
          />
        }
      >
        <div className="mb-6">
          <LibraryPageActionBar
            onManageMode={() => {}}
            onMove={() => setSidebarMode('move')}
            onDelete={onDeleteNodeIds}
            onCreate={() => {}}
            isLoading={isDeleting}
            manageDisabled={disableManageMultiple}
            moveContentDisabled={disableMoveContent}
            deleteContentDisabled={disableBatchDelete}
            hideManageMultiple={isSharedLibraryTopLevel}
          />
        </div>
        <Card className="py-7 px-8">
          {pageTitle}
          <div
            className="flex justify-center gap-4 flex-wrap min-h-[288px] md:h-auto md:justify-start md:gap-6"
            // Clears the "See in Library" highlight on the user's first
            // interaction with the grid — pointer (click, select, drag start)
            // or keyboard (tab + enter/space users never fire pointerdown).
            onPointerDownCapture={clearHighlight}
            onKeyDownCapture={clearHighlight}
          >
            {pageContent}
            <LibraryDragLayerV2 contents={sortedLibraryData?.data || []} />
          </div>
        </Card>
        {!isLibraryOnboardingCompleted && <LibraryOnboardingDialog isOpen />}
        {pageModal}
        <CopyUnownedContentDialog
          isOpen={unownedCopyResolver !== null}
          onCancel={() => unownedCopyResolver?.(null)}
          onConfirm={(choice) => unownedCopyResolver?.(choice)}
        />
        {sidebarMode !== null &&
          (selectedNodeIds.length > 0 || selectedResourceId) && (
            <PaperModalV2
              className={cn(
                'top-[135px] w-[414px] right-2 bottom-2 bg-blue-700',
              )}
              open={sidebarMode !== null}
              onClose={() => setSidebarMode(null)}
            >
              <MoveContent
                disabledFolderIds={disabledFolderIds}
                onMove={
                  sidebarMode === 'duplicate_and_move'
                    ? onDuplicateAndMove
                    : batchMode
                    ? onMoveBatchContent
                    : onMoveContent
                }
                initialScope={
                  sidebarMode === 'duplicate_and_move'
                    ? 'shared_library'
                    : queryParams.scope
                }
                mode={sidebarMode}
              />
            </PaperModalV2>
          )}
        <input
          ref={fileInput}
          multiple
          type="file"
          onChange={handleFileInputChange}
          style={{ position: 'absolute', top: -9999, opacity: 0 }}
        />
      </PageLayout>
    </Page>
  );
};

export default LibraryPageV2;
